import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { type RoleFormSchema, roleSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { RoleForm } from "@/forms/role-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditRoleModalProps extends ModalProps {
	roleId: string;
	onComplete?: () => Promise<void>;
}

export function EditRoleModal({ isOpen, onClose, roleId, onComplete }: EditRoleModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.roles[":roleId"].$get({
			param: { roleId },
		});
		const data = await responseHandler(response);
		return roleSchema.parse(data.role);
	});
	const methods = useForm<RoleFormSchema>({
		resolver: resolver(roleSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: RoleFormSchema) {
		const response = await client.roles[":roleId"].$patch({
			param: { roleId },
			json,
		});
		await responseHandler(response);
		// A rename changes the role's name inside every cached permission source, which is why
		// the server invalidates the bearers' sets. Dropping the caller's own copy here is the
		// other half — otherwise the renamed role keeps its old name for the rest of the session.
		await qc.invalidateQueries({
			predicate: invalidate(["roles", "citadel", "user-permissions-form"]),
		});
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("roles.rename_role")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit, console.error)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<RoleForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
